import { Component, OnInit, Input, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonSpinner, IonIcon, IonGrid, IonRow, IonCol, ToastController
 } from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ChartComponent, ApexChart, ApexPlotOptions, ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis, ApexTooltip, ApexFill, ApexLegend, ApexTitleSubtitle } from 'ng-apexcharts';

import { 
    MarvMessageComponent, MarvButtonComponent, MarvTagComponent, MarvDrawerComponent, 
    MarvInputComponent, MarvSelectItem, MarvSelectComponent, MarvToggleComponent  
} from '@marv/components';
import { MarvValidatorsService } from '@marv/services';
import { environment } from '@app/environment';
import { 
    OperationCard, OperationHourConsumptionReport, AreaQuotaHourConsumptionReport, 
    CustomerSectorHourQuotaConsumptionReport, ProfileQuotaConsumptionReport,
    TransferableHourQuotaReport, TransferableCustomerHourOrderQuota 
} from '@app/models';
import { QuotaService, LoggedUserService } from '@app/services';

class SectorTransferQuotaInfos{
    name: string;
    oldQuota: number;
    newQuota: number;
}

class ChartQuotasOptions {
    chart: ApexChart;
    plotOptions: ApexPlotOptions;
    series: ApexAxisChartSeries; 
    dataLabels: ApexDataLabels;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    tooltip: ApexTooltip;
    title: ApexTitleSubtitle;
    fill: ApexFill;
    legend: ApexLegend;
};

@Component({
    selector: 'app-quotas-global',
    templateUrl: './global.component.html',
    styleUrls: ['./global.component.scss'],
    imports: [
        CommonModule, IonSpinner, IonIcon, IonGrid, IonRow, IonCol, ReactiveFormsModule, TranslatePipe, ChartComponent,
        forwardRef(() => MarvMessageComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvTagComponent), 
        forwardRef(() => MarvDrawerComponent), forwardRef(() => MarvInputComponent), forwardRef(() => MarvSelectComponent),
        forwardRef(() => MarvToggleComponent)
    ]
})
export class QuotasGlobalComponent  implements OnInit {

    @Input({alias : 'operation', required : true}) operation: OperationCard;

    @ViewChild('drawerTransferQuota') drawerTransferQuota: MarvDrawerComponent;
    @ViewChild('drawerTransferQuotaGlobal') drawerTransferQuotaGlobal: MarvDrawerComponent;

    isFetching: boolean = false;
    isFetchingDownload: boolean = false; 
    reportQuota: OperationHourConsumptionReport|null;
      
    displayAsDaysForm: FormGroup = new FormGroup({
        displayAsDays: new FormControl(false, [Validators.required])
    });
      
    chartQuotasOptions: ChartQuotasOptions|null = null;
    
    constructor(
        public translateService: TranslateService,
        private quotaService: QuotaService,
        private toastController: ToastController,
        private loggedUserService: LoggedUserService,
    ) { }

    async ngOnInit() {    
        this.displayAsDaysForm.controls['displayAsDays'].setValue(false);    
        await this.loadQuotaReport();        
    }
    
    ngOnDestroy() {     
          
    }
    
    async loadQuotaReport(){        
        this.isFetching = true;
        this.reportQuota = null;    
        
        let loggedUser = this.loggedUserService.loggedUser;
        if(loggedUser){
            let report = await this.quotaService.loadHourQuotaReport(this.operation.id);
            if(report){
                let reportQuota = report.operationReports?.find(o => o.operation.id === this.operation.id);
                if(reportQuota){
                    this.reportQuota = reportQuota;   
                }
            }            
        }
        this.drawChart();       
        this.isFetching = false;
    }
    
    async drawChart(){
        let chartQuotasOptions: ChartQuotasOptions|null = null;
        if(this.reportQuota){
          
            let series: ApexAxisChartSeries = [];
                
            series.push({
                name: this.translateService.instant('CONSUMED'),
                data: [Math.round(this.reportQuota.consumedQuota)],
                color: "rgba(33, 150, 243, 0.8)"
            });   
            
            let availableQuota = this.reportQuota.globalQuota - this.reportQuota.consumedQuota;
            if (availableQuota > 0) {                   
                series.push({
                    name: this.translateService.instant('BALANCE'),
                    data: [Math.round(availableQuota)],
                    color: "rgba(104, 159, 56, 0.8)"
                });                
            } 
            else {                       
                series.push({
                    name: this.translateService.instant('DEPASSEMENT'),
                    data: [Math.round(-availableQuota)],
                    color: "rgba(229, 57, 53, 0.8)"
                }); 
            }

            chartQuotasOptions = {
                chart: {
                    type: 'bar',
                    stacked: true,
                    height: 175,
                    toolbar: {
                        show: false
                    },
                    parentHeightOffset: 0
                },
                plotOptions: {
                    bar: {
                        horizontal: true,                        
                    },
                    
                },
                dataLabels: {                    
                    offsetY: -30,
                    style: {
                        colors: ['#222']
                    },
                    formatter: function (val, opt) {                        
                        return (typeof val === 'number' && val >= 1000 ? Math.floor(val/10)/100 + "K" : val);
                    },
                },
                title: {
                    text: this.translateService.instant('QUOTA_GLOBAL_SUMMARY'),
                    align: 'center',
                    style: {
                        fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
                    }
                },
                series: series,
                xaxis: {
                    categories: [this.translateService.instant('QUOTAS')],
                    labels: {
                        formatter: function(val) {
                            let v = parseInt(val.toString());
                            return (v >= 1000 ? Math.floor(v/10)/100 + "K" : val).toString();
                        }
                    }
                },
                yaxis: {                    
                    labels: {
                        show: false
                    },
                    
                },
                tooltip: {
                    
                },
                fill: {
                    opacity: 1
                },
                legend: {
                    position: "top",
                    horizontalAlign: "left",
                    offsetX: 10
                }
            }
        }
        this.chartQuotasOptions = chartQuotasOptions;
    }

    async downloadReport(){
        this.isFetchingDownload = true;       
        let fileUrl = await this.quotaService.loadExcelHourQuotaReport(this.operation.id);
        let a = document.createElement('a'); 
        a.href = fileUrl;
        a.target = '_blank';
        a.click();
        this.isFetchingDownload = false;
    }
      
    getQuotaValue(value: number): number{
        return (this.displayAsDaysForm.controls['displayAsDays'].value ?  Math.round(value / 7) : value);
    } 
}
